'use client';

import * as React from 'react';
import { clsx } from 'clsx';
import { MapPin, Trash2, Heart } from 'lucide-react';
import { CandidateCardAvatar } from './CandidateCardAvatar';
import styles from './CandidateCardProfile.module.css';

export interface CandidateCardProfileSkill {
  id: number | string;
  name: string;
}

export interface CandidateCardProfileProps extends React.HTMLAttributes<HTMLDivElement> {
  candidate: {
    id: number | string;
    name: string;
    position_title?: string;
    highlights?: string;
    avatar?: string;
    blur?: boolean;
    location?: string;
    skills?: CandidateCardProfileSkill[];
  };
  ctaLabel?: string;
  onViewProfile?: () => void;
  onDelete?: () => void;
  onFavorite?: () => void;
  /** Background color of the card body panel. Defaults to the tinted blue from the design. */
  backgroundColor?: string;
  /** Show the trash / favourite action icons next to the CTA. Defaults to true. */
  showActions?: boolean;
  className?: string;
}

export const CandidateCardProfile = React.forwardRef<
  HTMLDivElement,
  CandidateCardProfileProps
>(
  (
    {
      candidate,
      ctaLabel = 'View profile',
      onViewProfile,
      onDelete,
      onFavorite,
      backgroundColor,
      showActions = true,
      className,
      ...props
    },
    ref,
  ) => {
    const skills = candidate.skills ?? [];

    return (
      <div
        ref={ref}
        id={`card-${candidate.id}`}
        className={clsx(styles.root, className)}
        {...props}
      >
        <div className={styles.header}>
          <div className={styles.headerText}>
            {candidate.location && (
              <span className={styles.locationChip}>
                <MapPin className={styles.locationIcon} aria-hidden />
                {candidate.location}
              </span>
            )}
            <h2 className={styles.name}>{candidate.name}</h2>
            {candidate.position_title && (
              <p className={styles.title}>{candidate.position_title}</p>
            )}
          </div>

          <div className={styles.avatarWrapper}>
            <CandidateCardAvatar
              id={`card-avatar-${candidate.id}`}
              name={candidate.name}
              src={candidate.avatar}
              blur={candidate.blur}
              size={80}
              className={styles.avatarImage}
              blurClassName={styles.avatarBlurred}
            />
          </div>
        </div>

        <div
          className={styles.body}
          style={backgroundColor ? { backgroundColor } : undefined}
        >
          {skills.length > 0 && (
            <div className={styles.skills}>
              {skills.map((skill) => (
                <span className={styles.skillChip} key={skill.id}>
                  {skill.name}
                </span>
              ))}
            </div>
          )}

          {candidate.highlights && (
            <p className={styles.description}>{candidate.highlights}</p>
          )}

          <div className={styles.footer}>
            <button type="button" className={styles.cta} onClick={onViewProfile}>
              {ctaLabel}
            </button>
            {showActions && (
              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.actionButton}
                  aria-label="Remove candidate"
                  onClick={onDelete}
                >
                  <Trash2 className={styles.actionIcon} aria-hidden />
                </button>
                <button
                  type="button"
                  className={styles.actionButton}
                  aria-label="Favorite candidate"
                  onClick={onFavorite}
                >
                  <Heart className={styles.actionIcon} aria-hidden />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

CandidateCardProfile.displayName = 'CandidateCardProfile';

export default CandidateCardProfile;
